import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import websocketService from '../services/websocket';
import TradingViewWidget from '../components/TradingViewWidget';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import {
    TrendingUp,
    LogOut,
    ArrowUp,
    ArrowDown,
    Clock,
    Search,
    Bell,
    Menu,
    ChevronDown
} from 'lucide-react';

const FuturesTradingPage = () => {
    const { symbol } = useParams();
    const { user, logout } = useAuthStore();
    const [selectedPair, setSelectedPair] = useState(symbol || 'BTCUSDT');
    const [orderType, setOrderType] = useState('limit');
    const [side, setSide] = useState('buy');
    const [price, setPrice] = useState('');
    const [stopPrice, setStopPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [ticker, setTicker] = useState(null);
    const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
    const [loading, setLoading] = useState(false);

    const [leverage, setLeverage] = useState(20);
    const [positions, setPositions] = useState([]);
    const [positionHistory, setPositionHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('open'); // 'open' or 'history'
    const [futuresBalance, setFuturesBalance] = useState(0);
    const [markets, setMarkets] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [closingPosition, setClosingPosition] = useState(null);

    useEffect(() => {
        fetchMarkets();
    }, []);

    // Sync selectedPair with URL symbol when navigating
    useEffect(() => {
        if (symbol && symbol !== selectedPair) {
            setSelectedPair(symbol);
            setPrice(''); // Reset price for new pair
            setQuantity(''); // Reset quantity
            setSearchTerm(''); // Clear search
        }
    }, [symbol]);

    useEffect(() => {
        if (selectedPair) {
            fetchMarketData();
            fetchPositions();
            fetchPositionHistory();
            fetchBalance();

            // WebSocket Integration
            const token = localStorage.getItem('token');
            if (token) {
                websocketService.connect(token);
                websocketService.subscribeToMarketData(selectedPair);

                const handleTicker = (data) => {
                    if (data.symbol === selectedPair) {
                        setTicker(prev => ({
                            ...prev,
                            lastPrice: data.price,
                            priceChangePercent: data.priceChangePercent,
                            highPrice: data.high,
                            lowPrice: data.low,
                            volume: data.volume,
                            quoteVolume: data.quoteVolume
                        }));

                        // Auto-update price for limit orders
                        setPrice(data.price);

                        // Real-time PnL update for positions
                        const currentPrice = parseFloat(data.price);
                        setPositions(prevPositions =>
                            prevPositions.map(pos => {
                                if (pos.symbol === data.symbol) {
                                    const entryPrice = parseFloat(pos.entryPrice);
                                    const amount = parseFloat(pos.amount);
                                    let pnl = 0;
                                    if (pos.side === 'LONG') {
                                        pnl = (currentPrice - entryPrice) * amount;
                                    } else {
                                        pnl = (entryPrice - currentPrice) * amount;
                                    }
                                    return { ...pos, unrealizedPnl: pnl };
                                }
                                return pos;
                            })
                        );
                    }
                };

                const handleOrderBook = (data) => {
                    if (data.symbol === selectedPair) {
                        setOrderBook(prev => {
                            return {
                                bids: data.bids.slice(0, 15),
                                asks: data.asks.slice(0, 15)
                            };
                        });
                    }
                };

                websocketService.on('ticker', handleTicker);
                websocketService.on('orderbook', handleOrderBook);

                return () => {
                    websocketService.off('ticker', handleTicker);
                    websocketService.off('orderbook', handleOrderBook);
                    websocketService.unsubscribeFromMarketData(selectedPair);
                };
            }
        }
    }, [selectedPair]);

    const fetchBalance = async () => {
        try {
            const res = await api.get('/wallet/balances');
            const usdtWallet = res.data.balances.find(b => b.token.symbol === 'USDT');
            if (usdtWallet) {
                setFuturesBalance(parseFloat(usdtWallet.futuresBalance || 0));
            }
        } catch (error) {
            console.error('Failed to fetch balance', error);
        }
    }

    const fetchMarkets = async () => {
        try {
            const res = await api.get('/markets');
            setMarkets(res.data.tradingPairs || []);
        } catch (e) {
            console.error('Failed to fetch markets');
        }
    };;

    const fetchMarketData = async () => {
        try {
            const [tickerRes, orderBookRes] = await Promise.all([
                api.get(`/markets/${selectedPair}/ticker`),
                api.get(`/markets/${selectedPair}/orderbook`)
            ]);

            const tickerData = tickerRes.data.ticker;
            setTicker({
                ...tickerData,
                lastPrice: tickerData.lastPrice,
                priceChangePercent: tickerData.priceChange24h,
                highPrice: tickerData.high24h,
                lowPrice: tickerData.low24h,
                volume: tickerData.volume24h
            });

            setOrderBook(orderBookRes.data.orderBook);
            if (orderType === 'limit' && !price) {
                setPrice(tickerRes.data.ticker.lastPrice);
            }
        } catch (error) {
            console.error('Failed to load market data', error);
        }
    };

    const fetchPositions = async () => {
        try {
            const res = await api.get('/futures/positions');
            setPositions(res.data.positions);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchPositionHistory = async () => {
        try {
            const res = await api.get('/futures/positions/history');
            setPositionHistory(res.data.positions || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePlaceOrder = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const orderData = {
                symbol: selectedPair,
                type: orderType.toUpperCase(),
                side: side.toUpperCase(),
                quantity: parseFloat(quantity),
                price: parseFloat(price), // Always send current price for margin calculation
                leverage: parseInt(leverage)
            };

            await api.post('/futures/order', orderData);
            toast.success('Futures Order placed successfully!');
            setQuantity('');
            fetchPositions();
            fetchBalance();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    const handleClosePosition = async (positionId) => {
        setClosingPosition(positionId);
        try {
            // Send current market price for accurate PnL calculation
            const currentPrice = ticker?.lastPrice ? parseFloat(ticker.lastPrice) : parseFloat(price);
            await api.post('/futures/close', { positionId, currentPrice });
            toast.success('Position closed successfully!');
            fetchPositions();
            fetchPositionHistory();
            fetchBalance();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to close position');
        } finally {
            setClosingPosition(null);
        }
    };

    // Calculate quantity based on percentage of available balance with leverage
    const handlePercentageClick = (percentage) => {
        if (!price || parseFloat(price) <= 0) {
            toast.error('Please enter a valid price first');
            return;
        }
        const currentPrice = parseFloat(price);
        const availableMargin = futuresBalance;
        // With leverage, user can control: (availableMargin * leverage) worth of position
        const maxNotional = availableMargin * leverage;
        const maxQuantity = maxNotional / currentPrice;
        const selectedQuantity = (maxQuantity * percentage) / 100;
        setQuantity(selectedQuantity.toFixed(6));
    };

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
    };

    return (
        <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
            {/* Navigation */}
            {/* Navigation */}
            <Navbar />

            <div className="max-w-[1600px] mx-auto p-4">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

                    {/* Left Column: Order Book & Recent Trades (Simulated) */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Order Book */}
                        <div className="bg-[#181a20] rounded-xl border border-gray-800 overflow-hidden">
                            <div className="p-4 border-b border-gray-800">
                                <h3 className="font-bold text-white text-sm">Order Book</h3>
                            </div>
                            <div className="p-2">
                                <div className="flex justify-between text-xs text-gray-500 mb-2 px-2">
                                    <span>Price(USDT)</span>
                                    <span>Amount(BTC)</span>
                                    <span>Total</span>
                                </div>
                                {/* Asks (Sells) - Red */}
                                <div className="space-y-1 mb-2">
                                    {orderBook.asks.slice(0, 10).reverse().map((ask, i) => {
                                        const askPrice = Array.isArray(ask) ? ask[0] : ask.price;
                                        const askQty = Array.isArray(ask) ? ask[1] : ask.quantity;
                                        return (
                                            <div key={i} className="flex justify-between text-xs px-2 hover:bg-[#2b3139] cursor-pointer" onClick={() => setPrice(askPrice)}>
                                                <span className="text-[#f6465d]">{parseFloat(askPrice).toFixed(8)}</span>
                                                <span className="text-gray-300">{parseFloat(askQty).toFixed(5)}</span>
                                                <span className="text-gray-500">{(parseFloat(askPrice) * parseFloat(askQty)).toFixed(2)}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Current Price */}
                                <div className="py-2 border-y border-gray-800 px-4 flex items-center gap-2">
                                    <span className={`text-xl font-bold ${parseFloat(ticker?.priceChangePercent) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                                        {parseFloat(ticker?.lastPrice || 0).toFixed(8)}
                                    </span>
                                    <span className="text-xs text-gray-400">${parseFloat(ticker?.lastPrice || 0).toFixed(8)}</span>
                                </div>

                                {/* Bids (Buys) - Green */}
                                <div className="space-y-1 mt-2">
                                    {orderBook.bids.slice(0, 10).map((bid, i) => {
                                        const bidPrice = Array.isArray(bid) ? bid[0] : bid.price;
                                        const bidQty = Array.isArray(bid) ? bid[1] : bid.quantity;
                                        return (
                                            <div key={i} className="flex justify-between text-xs px-2 hover:bg-[#2b3139] cursor-pointer" onClick={() => setPrice(bidPrice)}>
                                                <span className="text-[#0ecb81]">{parseFloat(bidPrice).toFixed(8)}</span>
                                                <span className="text-gray-300">{parseFloat(bidQty).toFixed(5)}</span>
                                                <span className="text-gray-500">{(parseFloat(bidPrice) * parseFloat(bidQty)).toFixed(2)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Middle Column: Chart */}
                    <div className="lg:col-span-2 space-y-4">
                        {/* Ticker Header */}
                        <div className="bg-[#181a20] rounded-xl border border-gray-800 p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <button className="flex items-center gap-2 text-2xl font-bold text-white hover:text-gray-300 transition">
                                        {selectedPair} <ChevronDown size={20} />
                                    </button>
                                    {/* Dropdown Placeholder */}
                                    <div className="absolute top-full left-0 mt-2 w-72 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 p-2">
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full bg-[#0b0e11] border border-gray-700 rounded px-2 py-1.5 text-sm text-white mb-2 focus:outline-none focus:border-blue-500"
                                        />
                                        <div className="max-h-80 overflow-y-auto space-y-0.5">
                                            {markets
                                                .filter(m => m.symbol.toLowerCase().includes(searchTerm.toLowerCase()))
                                                .filter(m => !/((DOWN|UP|BULL|BEAR)USDT|^1000)/.test(m.symbol)) // Exclude leveraged tokens
                                                .sort((a, b) => parseFloat(b.volume24h || 0) - parseFloat(a.volume24h || 0))
                                                .map(m => (
                                                    <Link
                                                        key={m.id}
                                                        to={`/futures/${m.symbol}`}
                                                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#2b3139] rounded text-sm text-white"
                                                    >
                                                        <img
                                                            src={`https://assets.coincap.io/assets/icons/${m.baseToken?.symbol?.toLowerCase()}@2x.png`}
                                                            alt=""
                                                            className="w-5 h-5 rounded-full"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                        <span>{m.symbol}</span>
                                                    </Link>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="relative group">
                                    <div className="text-xs font-medium px-2 py-1 bg-gray-800 rounded text-[#FCD535] cursor-pointer flex items-center gap-1 hover:bg-gray-700 transition">
                                        Futures <ChevronDown size={12} />
                                    </div>
                                    <div className="absolute top-full left-0 mt-2 w-32 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                                        <Link to={`/trade/${selectedPair}`} className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Spot</Link>
                                        <Link to={`/futures/${selectedPair}`} className="block px-4 py-2 text-sm text-[#FCD535] bg-[#2b3139] font-bold">Futures</Link>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
                                <div>
                                    <div className="text-gray-500 text-xs">24h Change</div>
                                    <div className={`font-medium ${parseFloat(ticker?.priceChangePercent) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                                        {parseFloat(ticker?.priceChangePercent || 0).toFixed(2)}%
                                    </div>
                                </div>
                                <div className="hidden sm:block">
                                    <div className="text-gray-500 text-xs">24h High</div>
                                    <div className="text-white font-medium">{parseFloat(ticker?.highPrice || 0).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 text-xs">24h Low</div>
                                    <div className="text-white font-medium">{parseFloat(ticker?.lowPrice || 0).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 text-xs">24h Volume</div>
                                    <div className="text-white font-medium">{parseFloat(ticker?.volume || 0).toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Chart Area */}
                        <div className="bg-[#181a20] rounded-xl border border-gray-800 h-[300px] sm:h-[500px] relative overflow-hidden">
                            <TradingViewWidget symbol={selectedPair} isCustom={ticker?.baseToken?.isCustom} />
                        </div>

                        {/* Positions Panel (Futures Specific) */}
                        <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4 overflow-auto">
                            {/* Tabs */}
                            <div className="flex gap-4 mb-4 border-b border-gray-800">
                                <button
                                    onClick={() => setActiveTab('open')}
                                    className={`pb-2 text-sm font-semibold transition ${activeTab === 'open' ? 'text-[#FCD535] border-b-2 border-[#FCD535]' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Open Positions ({positions.length})
                                </button>
                                <button
                                    onClick={() => { setActiveTab('history'); fetchPositionHistory(); }}
                                    className={`pb-2 text-sm font-semibold transition ${activeTab === 'history' ? 'text-[#FCD535] border-b-2 border-[#FCD535]' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Position History ({positionHistory.length})
                                </button>
                            </div>

                            {/* Open Positions Table */}
                            {activeTab === 'open' && (
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="text-gray-600 border-b border-gray-800">
                                            <th className="pb-2">Symbol</th>
                                            <th className="pb-2">Size</th>
                                            <th className="pb-2">Entry Price</th>
                                            <th className="pb-2">Liq. Price</th>
                                            <th className="pb-2">Margin</th>
                                            <th className="pb-2">PNL</th>
                                            <th className="pb-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {positions.map(pos => (
                                            <tr key={pos.id} className="border-b border-gray-900 hover:bg-gray-900/50 transition">
                                                <td className="py-3 font-medium flex items-center gap-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${pos.side === 'LONG' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                                                        {pos.side}
                                                    </span>
                                                    {pos.symbol}
                                                    <span className="text-gray-500 text-xs">x{pos.leverage}</span>
                                                </td>
                                                <td className="text-gray-300">{parseFloat(pos.amount).toFixed(4)}</td>
                                                <td className="text-gray-300">{parseFloat(pos.entryPrice).toFixed(2)}</td>
                                                <td className="text-orange-500">{pos.liquidationPrice ? parseFloat(pos.liquidationPrice).toFixed(2) : 'N/A'}</td>
                                                <td className="text-gray-300">{parseFloat(pos.margin).toFixed(2)} USDT</td>
                                                <td className={`font-bold ${parseFloat(pos.unrealizedPnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {parseFloat(pos.unrealizedPnl).toFixed(2)} USDT
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => handleClosePosition(pos.id)}
                                                        disabled={closingPosition === pos.id}
                                                        className="text-xs bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-white transition disabled:opacity-50"
                                                    >
                                                        {closingPosition === pos.id ? 'Closing...' : 'Close'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {positions.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-8 text-gray-600">No Open Positions</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {/* Position History Table */}
                            {activeTab === 'history' && (
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="text-gray-600 border-b border-gray-800">
                                            <th className="pb-2">Symbol</th>
                                            <th className="pb-2">Side</th>
                                            <th className="pb-2">Size</th>
                                            <th className="pb-2">Entry</th>
                                            <th className="pb-2">Exit</th>
                                            <th className="pb-2">PnL</th>
                                            <th className="pb-2">Closed At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {positionHistory.map(pos => {
                                            const pnl = parseFloat(pos.realizedPnl || 0);
                                            return (
                                                <tr key={pos.id} className="border-b border-gray-900 hover:bg-gray-900/50 transition">
                                                    <td className="py-3 font-medium">{pos.symbol}</td>
                                                    <td>
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${pos.side === 'LONG' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                                                            {pos.side}
                                                        </span>
                                                    </td>
                                                    <td className="text-gray-300">{parseFloat(pos.closedAmount || pos.amount || 0).toFixed(4)}</td>
                                                    <td className="text-gray-300">${parseFloat(pos.entryPrice).toFixed(2)}</td>
                                                    <td className="text-gray-300">${pos.exitPrice ? parseFloat(pos.exitPrice).toFixed(2) : '-'}</td>
                                                    <td className={`font-bold ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} USDT
                                                    </td>
                                                    <td className="text-gray-500">{new Date(pos.updatedAt).toLocaleString()}</td>
                                                </tr>
                                            );
                                        })}
                                        {positionHistory.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-8 text-gray-600">No Position History</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Order Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
                            {/* Leverage Slider (Futures Specific) */}
                            <div className="mb-6 bg-[#0b0e11] p-3 rounded-lg">
                                <div className="flex justify-between text-xs text-gray-400 mb-2">
                                    <span>Leverage</span>
                                    <span className="text-[#FCD535] font-bold">{leverage}x</span>
                                </div>
                                <input
                                    type="range"
                                    min="1"
                                    max="125"
                                    value={leverage}
                                    onChange={(e) => setLeverage(e.target.value)}
                                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FCD535]"
                                />
                                <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                                    <span>1x</span>
                                    <span>125x</span>
                                </div>
                            </div>

                            <div className="flex gap-2 mb-6 bg-[#0b0e11] p-1 rounded-lg">
                                <button
                                    onClick={() => setSide('buy')}
                                    className={`flex-1 py-2 rounded-md font-bold text-sm transition ${side === 'buy' ? 'bg-[#0ecb81] text-white' : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    Buy / Long
                                </button>
                                <button
                                    onClick={() => setSide('sell')}
                                    className={`flex-1 py-2 rounded-md font-bold text-sm transition ${side === 'sell' ? 'bg-[#f6465d] text-white' : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    Sell / Short
                                </button>
                            </div>

                            <div className="flex justify-between text-xs text-gray-400 mb-4">
                                <span>Avail:</span>
                                <span className="text-white font-medium">{futuresBalance.toFixed(2)} USDT</span>
                            </div>

                            <form onSubmit={handlePlaceOrder} className="space-y-4">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Order Type</label>
                                    <select
                                        value={orderType}
                                        onChange={(e) => setOrderType(e.target.value)}
                                        className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                    >
                                        <option value="limit">Limit</option>
                                        <option value="market">Market</option>
                                        <option value="stop_loss">Stop Loss</option>
                                        <option value="stop_loss_limit">Stop Limit</option>
                                    </select>
                                </div>

                                {['stop_loss', 'stop_loss_limit'].includes(orderType) && (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Stop Price (USDT)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={stopPrice}
                                                onChange={(e) => setStopPrice(e.target.value)}
                                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                                                placeholder="0.00"
                                            />
                                            <span className="absolute left-3 top-2 text-gray-500 text-sm">Stop</span>
                                        </div>
                                    </div>
                                )}

                                {['limit', 'stop_loss_limit'].includes(orderType) && (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Price (USDT)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={price}
                                                onChange={(e) => setPrice(e.target.value)}
                                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                                                placeholder="0.00"
                                            />
                                            <span className="absolute left-3 top-2 text-gray-500 text-sm">Price</span>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Amount ({selectedPair.replace('USDT', '')})</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.000001"
                                            value={quantity}
                                            onChange={(e) => setQuantity(e.target.value)}
                                            className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-right"
                                            placeholder="0.00"
                                        />
                                        <span className="absolute left-3 top-2 text-gray-500 text-sm">Amount</span>
                                    </div>
                                </div>

                                {/* Percentage Buttons */}
                                <div className="flex justify-between gap-1">
                                    {[25, 50, 75, 100].map(pct => (
                                        <button
                                            key={pct}
                                            type="button"
                                            onClick={() => handlePercentageClick(pct)}
                                            className="flex-1 bg-[#0b0e11] text-gray-400 text-xs py-1.5 rounded border border-gray-700 hover:bg-gray-700 hover:text-white transition"
                                        >
                                            {pct}%
                                        </button>
                                    ))}
                                </div>

                                <div className="pt-2">
                                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                                        <span>Total</span>
                                        <span className="text-white">{(parseFloat(price || 0) * parseFloat(quantity || 0)).toFixed(2)} USDT</span>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full py-3 rounded-lg font-bold text-white transition ${side === 'buy' ? 'bg-[#0ecb81] hover:bg-[#0ecb81]/90' : 'bg-[#f6465d] hover:bg-[#f6465d]/90'
                                            }`}
                                    >
                                        {loading ? 'Processing...' : `${side === 'buy' ? 'Buy / Long' : 'Sell / Short'} ${selectedPair.replace('USDT', '')}`}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FuturesTradingPage;
